import { connect } from 'react-redux';
import cn from 'classnames';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { KnmSignatureInfoStatusesEnum } from 'APIPackage/KnmModule/enums/KnmSignatureInfoStatusesEnum';
import { IKnmSignatureInfo } from 'APIPackage/KnmModule/interfaces/IKnmSignatureInfo';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { KnmSignInfoMoreAccordion } from 'KnmPackage/KnmSignInfoModule/components/KnmSignInfoMoreAccordion';
import { DatePicker } from 'UIPackage/MainModule/components/DatePicker';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { getSignStatus } from 'KnmPackage/KnmSignInfoModule/common/getSignStatus';
import { getValidityPeriod } from 'KnmPackage/KnmSignInfoModule/common/getValidityPeriod';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';

import styles from './PlanSignInfoModal.module.scss';

interface IPlanSignInfoModalProps {
  visible: boolean;
  plan?: IKnmPlan;
  onClose: () => void;
}

export const PlanSignInfoModalWithoutStore = ({ visible, plan, onClose }: IPlanSignInfoModalProps) => {
  const signatureInfoMoreFieldBlockStyle = cn('shared-field-row', styles.SignatureInfoMoreFieldBlock);

  return (
    <Modal visible={visible} onClose={onClose}>
      <div className={styles.PlanSignInfoModalContainer}>
        <h2 className="shared-title">Подписание плана</h2>
        {plan?.knmPlanData?.signedXlsxFile?.signatureInfo?.map((signatureInfo: IKnmSignatureInfo) => (
          <div className={styles.SignatureInfo} key={signatureInfo.signatureId}>
            <div className={styles.SignatureResultFieldBlock}>
              <div className="shared-group-margin">
                <label className="shared-group-label" htmlFor="signatureResult">
                  Результат подписания
                </label>
              </div>
              <TextInput
                className={styles.SignatureResultField}
                id="signatureResult"
                name="signatureResult"
                value={`План подписан усиленной КЭП${
                  signatureInfo?.signedDate
                    ? ` ${formatDate({ date: signatureInfo?.signedDate, dateFormat: DATE_FORMAT_LONG_FNS })}`
                    : ''
                }`}
                disabled
              />
            </div>

            <div className={cn('shared-field-row', styles.SignatureStatusFieldBlock)}>
              <div className="shared-field-row-block_width-small">
                <label className="shared-field-label_row" htmlFor="signatureStatus">
                  Статус подписи
                </label>
              </div>
              <TextInput
                className="shared-row-field"
                id="signatureStatus"
                name="signatureStatus"
                value={getSignStatus(signatureInfo.status, signatureInfo.errorMessage)}
                disabled
              />
            </div>

            {signatureInfo?.status !== KnmSignatureInfoStatusesEnum.IN_PROCESS && (
              <>
                <div className={cn('shared-field-row', styles.SignatureCertNumberFieldBlock)}>
                  <div className="shared-field-row-block_width-small">
                    <label className="shared-field-label_row" htmlFor="signatureCertNumber">
                      Сертификат
                    </label>
                  </div>
                  <TextInput
                    className="shared-row-field"
                    id="signatureCertNumber"
                    name="signatureCertNumber"
                    value={signatureInfo?.certInfo?.certNumber}
                    disabled
                  />
                </div>

                <div className={cn('shared-field-row', styles.SignatureIssuerNameFieldBlock)}>
                  <div className="shared-field-row-block_width-small">
                    <label className="shared-field-label_row" htmlFor="signatureIssuerName">
                      Владелец
                    </label>
                  </div>
                  <TextInput
                    className="shared-row-field"
                    id="signatureIssuerName"
                    name="signatureIssuerName"
                    value={signatureInfo?.certInfo?.issuer?.name}
                    disabled
                  />
                </div>

                <div className={cn('shared-field-row', styles.SignatureRoleFieldBlock)}>
                  <div className="shared-field-row-block_width-small">
                    <label className="shared-field-label_row" htmlFor="signatureRole">
                      Должность
                    </label>
                  </div>
                  <TextInput
                    className="shared-row-field"
                    id="signatureRole"
                    name="signatureRole"
                    value={signatureInfo?.certInfo?.subject?.role}
                    disabled
                  />
                </div>

                <div className={cn('shared-field-row', styles.SignatureCertValidFromFieldBlock)}>
                  <div className="shared-field-row-block_width">
                    <label className="shared-field-label_row" htmlFor="signatureCertValidFrom">
                      Действителен с
                    </label>
                  </div>
                  <DatePicker
                    className="shared-row-field"
                    id="signatureCertValidFrom"
                    name="signatureCertValidFrom"
                    value={formatDate({ date: signatureInfo?.certInfo?.validFrom, dateFormat: DATE_FORMAT_LONG_FNS })}
                    disabled
                  />
                </div>

                <div className={cn('shared-field-row', styles.SignatureCertValidToFieldBlock)}>
                  <div className="shared-field-row-block_width">
                    <label className="shared-field-label_row" htmlFor="signatureCertValidTo">
                      Действителен по
                    </label>
                  </div>
                  <DatePicker
                    className="shared-row-field"
                    id="signatureCertValidTo"
                    name="signatureCertValidTo"
                    value={formatDate({ date: signatureInfo?.certInfo?.validTo, dateFormat: DATE_FORMAT_LONG_FNS })}
                    disabled
                  />
                </div>
              </>
            )}
            <KnmSignInfoMoreAccordion>
              <div className={signatureInfoMoreFieldBlockStyle}>
                <div className="shared-field-row-block_width-small">
                  <label className="shared-field-label_row" htmlFor="versionNumber">
                    Версия
                  </label>
                </div>
                <TextInput
                  className="shared-row-field"
                  id="versionNumber"
                  name="versionNumber"
                  value={signatureInfo?.certInfo?.versionNumber}
                  disabled
                />
              </div>
              <div className={signatureInfoMoreFieldBlockStyle}>
                <div className="shared-field-row-block_width-small">
                  <label className="shared-field-label_row" htmlFor="certNumber">
                    Серийный номер
                  </label>
                </div>
                <TextInput
                  className="shared-row-field"
                  id="certNumber"
                  name="certNumber"
                  value={signatureInfo?.certInfo?.certNumber}
                  disabled
                />
              </div>
              <div className={signatureInfoMoreFieldBlockStyle}>
                <div className="shared-field-row-block_width-small">
                  <label className="shared-field-label_row" htmlFor="argorithmName">
                    Алгоритм подписывания ЦС
                  </label>
                </div>
                <TextInput
                  className="shared-row-field"
                  id="argorithmName"
                  name="argorithmName"
                  value={signatureInfo?.certInfo?.argorithmName}
                  disabled
                />
              </div>
              <div className={signatureInfoMoreFieldBlockStyle}>
                <div className="shared-field-row-block_width-small">
                  <label className="shared-field-label_row" htmlFor="issuerName">
                    Имя издателя
                  </label>
                </div>
                <TextInput
                  className="shared-row-field"
                  id="issuerName"
                  name="issuerName"
                  value={signatureInfo?.certInfo?.issuer?.name}
                  disabled
                />
              </div>
              <div className={signatureInfoMoreFieldBlockStyle}>
                <div className="shared-field-row-block_width-small">
                  <label className="shared-field-label_row" htmlFor="validityPeriod">
                    Период действия
                  </label>
                </div>
                <TextInput
                  className="shared-row-field"
                  id="validityPeriod"
                  name="validityPeriod"
                  value={getValidityPeriod(signatureInfo?.certInfo?.validFrom, signatureInfo?.certInfo?.validTo)}
                  disabled
                />
              </div>
              <div className={signatureInfoMoreFieldBlockStyle}>
                <div className="shared-field-row-block_width-small">
                  <label className="shared-field-label_row" htmlFor="subjectName">
                    Имя субъекта
                  </label>
                </div>
                <TextInput
                  className="shared-row-field"
                  id="subjectName"
                  name="subjectName"
                  value={signatureInfo?.certInfo?.subject?.name}
                  disabled
                />
              </div>
              <div className={signatureInfoMoreFieldBlockStyle}>
                <div className="shared-field-row-block_width-small">
                  <label className="shared-field-label_row" htmlFor="subjectPublicKeyInfo">
                    Данные открытого ключа субъекта
                  </label>
                </div>
                <TextInput
                  className="shared-row-field"
                  id="subjectPublicKeyInfo"
                  name="subjectPublicKeyInfo"
                  value={signatureInfo?.certInfo?.subjectPublicKeyInfo}
                  disabled
                />
              </div>
            </KnmSignInfoMoreAccordion>
          </div>
        ))}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  plan: state.plan.data,
});

export const PlanSignInfoModal = connect(mapStateToProps)(PlanSignInfoModalWithoutStore);
